// Platform administration (PLATFORM users): tenants, their limits, and working inside one of them.
import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, LogIn } from 'lucide-react';
import { api, errorText } from '@/lib/api';
import { PageHeader, DataTable, ErrorBox, Field } from '@/components/common';
import { Button } from '@/components/ui/button';
import { Input, Select } from '@/components/ui/input';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { StatusBadge } from '@/components/ui/badge';
import { toast } from '@/store/toast';
import { useAuth, type Tenant } from '@/store/auth';
import { enumLabel } from '@/lib/utils';
import { t } from '@/i18n';

interface TenantRow extends Omit<Tenant, 'modules'> {
  modules: string;           // "CALLCENTER", "PBX" or "CALLCENTER,PBX"
  extensions: number;
  max_cps: number; max_concurrent: number; max_agents: number;
  agents: number; users: number; live_calls: number; active_campaigns: number;
}

const limitText = (n: number) => (Number(n) > 0 ? String(n) : t('no limit'));
// the product modules (migration 026) as the platform admin picks them; the form offers "both" only to a tenant that
// already has both (one product per tenant on this form for now, 2026-10-15; the API still accepts both)
const PRODUCTS: [string, string][] = [['CALLCENTER', 'Call center'], ['PBX', 'IP PBX'], ['CALLCENTER,PBX', 'Call center and IP PBX']];
const moduleText = (m: string | undefined) => t(PRODUCTS.find(([k]) => k === (m || 'CALLCENTER'))?.[1] ?? String(m));
// what a module switch stops (PATCH answer), in words
function warningText(w: string): string {
  const [code, n] = w.split(':');
  const text: Record<string, string> = {
    PBX_OFF_EXTENSIONS: '{{n}} extensions stop ringing', PBX_OFF_INBOUND_ROUTES: '{{n}} numbers go to a PBX destination and stop working',
    CALLCENTER_OFF_ACTIVE_CAMPAIGNS: '{{n}} active campaigns stop dialing', CALLCENTER_OFF_AGENTS: '{{n}} agents can no longer work',
    CALLCENTER_OFF_INBOUND_ROUTES: '{{n}} numbers go to an agent or queue and stop working',
  };
  return text[code] ? t(text[code], { n }) : w;
}

// work inside another tenant (or go back to your own): a new token, every cached page dropped
export function useSwitchTenant() {
  const qc = useQueryClient();
  const nav = useNavigate();
  return async (tenantId: number) => {
    try {
      const r = await api<{ token: string; tenant: Tenant }>('/platform/switch', { body: { tenant_id: tenantId } });
      const { user, homeTenantId, setSession, setTenant } = useAuth.getState();
      if (!user) return;
      setSession(r.token, { ...user, tenant_id: r.tenant.id });
      setTenant(r.tenant, homeTenantId ?? user.tenant_id);
      qc.clear();
      nav('/admin');
      toast.ok(t('Working in {{name}}', { name: r.tenant.name }));
    } catch (e) { toast.error(errorText(e)); }
  };
}

export function TenantsPage() {
  const q = useQuery({ queryKey: ['tenants'], queryFn: () => api<{ rows: TenantRow[] }>('/platform/tenants') });
  const current = useAuth((s) => s.tenant?.id ?? s.user?.tenant_id);
  const [edit, setEdit] = React.useState<TenantRow | null | undefined>(undefined);
  const switchTo = useSwitchTenant();
  return (
    <div>
      <PageHeader title={t('Tenants')} description={t('Each tenant is a company with its own SIP domain, agents, numbers and data.')}>
        <Button onClick={() => setEdit(null)}><Plus /> {t('New tenant')}</Button>
      </PageHeader>
      <ErrorBox error={q.error} />
      <DataTable rowKey={(r) => r.id} rows={q.data?.rows} columns={[
        { key: 'name', label: t('Name') }, { key: 'slug', label: t('Sign-in name') }, { key: 'sip_domain', label: t('SIP domain') },
        { key: 'status', label: t('Status'), render: (r) => <StatusBadge value={r.status} /> },
        { key: 'modules', label: t('Product'), render: (r) => <span data-testid={`tenant-modules-${r.slug}`}>{moduleText(r.modules)}</span> },
        { key: 'agents', label: t('Agents'), render: (r) => `${r.agents} / ${limitText(r.max_agents)}` },
        { key: 'live_calls', label: t('Live calls'), render: (r) => `${r.live_calls} / ${limitText(r.max_concurrent)}` },
        { key: 'max_cps', label: t('Max CPS'), render: (r) => limitText(r.max_cps) },
        { key: 'users', label: t('Users') }, { key: 'active_campaigns', label: t('Active campaigns') }, { key: 'extensions', label: t('Extensions') }]}
        actions={(r) => <div className="flex gap-1">
          <Button size="sm" variant="outline" disabled={r.id === current} onClick={() => switchTo(r.id)} data-testid={`tenant-open-${r.slug}`}>
            <LogIn /> {r.id === current ? t('Current') : t('Open')}
          </Button>
          <Button size="icon" variant="ghost" aria-label={t('Edit {{name}}', { name: r.name })} onClick={() => setEdit(r)}><Pencil /></Button>
        </div>} />
      <Dialog open={edit !== undefined} onOpenChange={(o) => !o && setEdit(undefined)}>
        {edit !== undefined && <DialogContent title={edit ? t('Edit {{name}}', { name: edit.name }) : t('New tenant')}>
          <TenantForm tenant={edit} onDone={() => { setEdit(undefined); q.refetch(); }} />
        </DialogContent>}
      </Dialog>
    </div>
  );
}

function TenantForm({ tenant, onDone }: { tenant: TenantRow | null; onDone: () => void }) {
  const [v, setV] = React.useState<Record<string, string>>(() => ({
    name: tenant?.name ?? '', slug: tenant?.slug ?? '', sip_domain: tenant?.sip_domain ?? '', status: tenant?.status ?? 'ACTIVE',
    max_cps: String(tenant?.max_cps ?? 0), max_concurrent: String(tenant?.max_concurrent ?? 0), max_agents: String(tenant?.max_agents ?? 0),
    admin_username: '', admin_password: '', modules: tenant?.modules || 'CALLCENTER',
  }));
  const [error, setError] = React.useState<unknown>(null);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setV((s) => ({ ...s, [k]: e.target.value }));
  const domainChanged = !!tenant && v.sip_domain !== tenant.sip_domain;
  async function submit(e: React.FormEvent) {
    e.preventDefault(); setError(null);
    const limits = { max_cps: Number(v.max_cps) || 0, max_concurrent: Number(v.max_concurrent) || 0, max_agents: Number(v.max_agents) || 0 };
    try {
      if (tenant) {
        const r = await api<{ warnings?: string[] }>(`/platform/tenants/${tenant.id}`, { method: 'PATCH',
          body: { name: v.name, sip_domain: v.sip_domain, status: v.status, modules: v.modules.split(','), ...limits } });
        toast.ok(t('Tenant saved'));
        for (const w of r.warnings ?? []) toast.error(warningText(w));
      } else {
        await api('/platform/tenants', { body: { name: v.name, slug: v.slug, sip_domain: v.sip_domain, modules: v.modules.split(','), ...limits,
                                                 admin: { username: v.admin_username, password: v.admin_password } } });
        toast.ok(t('Tenant created. Its admin signs in as {{login}}', { login: `${v.admin_username}@${v.slug}` }));
      }
      onDone();
    } catch (err) { setError(err); }
  }
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2">
      <Field help="tenants.name" label={t('Name')}><Input name="name" value={v.name} onChange={set('name')} required /></Field>
      <Field help="tenants.slug" label={t('Sign-in name')} hint={t('users sign in as username@this')}>
        <Input name="slug" value={v.slug} onChange={set('slug')} required disabled={!!tenant} pattern="[a-z0-9][a-z0-9\-]{1,39}" />
      </Field>
      <Field help="tenants.sip_domain" label={t('SIP domain')} hint={domainChanged ? t('Every agent moves to the new domain; their phones must register again') : t('phones register as extension@this')}>
        <Input name="sip_domain" value={v.sip_domain} onChange={set('sip_domain')} required />
      </Field>
      <Field help="tenants.modules" label={t('Product')} hint={tenant && v.modules !== (tenant.modules || 'CALLCENTER') ? t('Switching a product off keeps its data; its pages and calls stop.') : t('what this tenant runs')}>
        <Select name="modules" value={v.modules} onChange={set('modules')}>{PRODUCTS.filter(([k]) => !k.includes(',') || tenant?.modules === k).map(([k, l]) => <option key={k} value={k}>{t(l)}</option>)}</Select>
      </Field>
      {tenant && <Field help="tenants.status" label={t('Status')}><Select name="status" value={v.status} onChange={set('status')}><option value="ACTIVE">{enumLabel('ACTIVE')}</option><option value="DISABLED">{enumLabel('DISABLED')}</option></Select></Field>}
      <Field help="tenants.max_cps" label={t('Max calls per second')} hint={t('0 = no limit')}><Input name="max_cps" type="number" min={0} value={v.max_cps} onChange={set('max_cps')} /></Field>
      <Field help="tenants.max_concurrent" label={t('Max calls at once')} hint={t('0 = no limit')}><Input name="max_concurrent" type="number" min={0} value={v.max_concurrent} onChange={set('max_concurrent')} /></Field>
      <Field help="tenants.max_agents" label={t('Max agents')} hint={t('0 = no limit')}><Input name="max_agents" type="number" min={0} value={v.max_agents} onChange={set('max_agents')} /></Field>
      {!tenant && (<>
        <Field help="tenants.admin_username" label={t('First admin username')}><Input name="admin_username" value={v.admin_username} onChange={set('admin_username')} required /></Field>
        <Field help="tenants.admin_password" label={t('First admin password')} hint={t('at least 8 characters')}>
          <Input name="admin_password" type="password" autoComplete="new-password" value={v.admin_password} onChange={set('admin_password')} required minLength={8} />
        </Field>
      </>)}
      <div className="sm:col-span-2"><ErrorBox error={error} /></div>
      <div className="flex justify-end gap-2 sm:col-span-2"><Button type="submit">{tenant ? t('Save') : t('Create tenant')}</Button>
</div>
    </form>
  );
}
